import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchPriceListsQuery } from 'api';
import { Form, TablePaginationConfig } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { OrderType } from 'typings';

const initialFilters: Record<string, string | undefined> = {
  supplierId: undefined,
  createdDateFrom: undefined,
  createdDateTo: undefined,
  lastUpdatedDateFrom: undefined,
  lastUpdatedDateTo: undefined,
  expireDateFrom: undefined,
  expireDateTo: undefined,
};

export const usePriceList = () => {
  const { id } = useParams();
  const [filtersForm] = Form.useForm();
  const [filters, setFilters] = useState(initialFilters);
  const navigate = useNavigate();
  const [openFilters, setOpenFilters] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | undefined>();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [search, setSearch] = useState('');
  const [sorter, setSorter] = useState({ field: '', order: '' });
  const { data: priceList, isLoading } = useFetchPriceListsQuery({
    page: pagination.current - 1,
    size: pagination.pageSize,
    sort: sorter.field && sorter.order ? `${sorter.field},${sorter.order}` : undefined,
    search: search || undefined,
    brandId: id || '',
    supplierId: filters.supplierId,
    createdDateFrom: filters.createdDateFrom,
    createdDateTo: filters.createdDateTo,
    lastUpdatedDateFrom: filters.lastUpdatedDateFrom,
    lastUpdatedDateTo: filters.lastUpdatedDateTo,
    expireDateFrom: filters.expireDateFrom,
    expireDateTo: filters.expireDateTo,
  });

  const onFiltersApply = useCallback(() => {
    const { supplierId, createdDate, expireDate } = filtersForm.getFieldsValue();
    const _filters: any = {
      ...(supplierId && { supplierId }),
      ...(createdDate?.[0] && { createdDateFrom: dateFormatter(createdDate[0], 'start') }),
      ...(createdDate?.[1] && { createdDateTo: dateFormatter(createdDate[1], 'end') }),
      ...(expireDate?.[0] && { expireDateFrom: dateFormatter(expireDate?.[0], 'start') }),
      ...(expireDate?.[1] && { expireDateTo: dateFormatter(expireDate?.[1], 'end') }),
    };
    setFilters(_filters);
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setOpenFilters(false);
  }, [filtersForm]);

  const resetFilters = useCallback(() => setFilters({}), []);

  const onFiltersStateChange = useCallback(() => {
    setOpenFilters((oState) => !oState);
  }, []);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<any> | SorterResult<any>[]
  ): void => {
    const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
    const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
    const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
    setPagination({
      current: pagination.current || 0,
      pageSize: pagination.pageSize || 10,
    });
    setSorter({
      field: sortField as string,
      order: order as OrderType,
    });
  };

  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}`);
    },
    [navigate]
  );

  const onHandleDelete = useCallback((id: string | undefined) => {
    setDeleteItemId(id);
  }, []);

  const onDeleteModalClose = useCallback(() => {
    setDeleteItemId(undefined);
  }, []);

  const onAddModalStateChange = useCallback(() => {
    setOpenAddModal((oState) => !oState);
  }, []);

  return {
    routeChange,
    handleTableChange,
    priceList,
    pagination,
    isLoading,
    setSearch,
    onHandleDelete,
    onDeleteModalClose,
    deleteItemId,
    openAddModal,
    onAddModalStateChange,
    onFiltersApply,
    resetFilters,
    filtersForm,
    openFilters,
    onFiltersStateChange,
  };
};
